import { PriceFormatOptions } from '../../../i18n';
import { isBlank, isEmpty, isPresent, last, pad } from '../../../utils';
import {
  AmDurationType,
  AmHolidayType,
  AmLocation,
  asDateString,
  Calendar,
  Day,
  daysBetween,
  getMonth,
  Month,
  TravelDates,
} from '../../../common';
import { AmAvailability, amDayIsSearchable } from '../../../store';

// == Date input handling ======================================================

const LOCAL_DATE_FORMAT = /^(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.((?:20)\d{2})$/;

export enum DateInput {
  DEPARTURE = 'departureInput',
  RETURN = 'returnInput',
}

export interface DateRange {
  rangeStart: string;
  rangeEnd: string;
}

export const fromIso8601 = (isoDate: string): string => {
  if (isBlank(isoDate)) {
    return '';
  }
  const [year, month, date]: string[] = isoDate.split('-');
  return `${date}.${month}.${year}`;
};

export const toIso8601 = (localDate: string): string => {
  if (isBlank(localDate)) {
    return undefined;
  }
  const [date, month, year]: string[] = localDate.split('.').map((part: string) => pad(part));
  return `${year}-${month}-${date}`;
};

export const validateLocalDate = (localDate: string, { rangeStart, rangeEnd }: DateRange): boolean => {
  if (isBlank(localDate)) {
    return true;
  }

  if (!LOCAL_DATE_FORMAT.test(localDate)) {
    return false;
  }

  const iso8601Date: string = toIso8601(localDate);
  const checksum: string = asDateString(new Date(iso8601Date));
  return iso8601Date === checksum && rangeStart <= iso8601Date && iso8601Date <= rangeEnd;
};

export const getCalendarRange = (calendar: Calendar): DateRange => ({
  rangeStart: calendar.months[0].days[0].id,
  rangeEnd: last(last(calendar.months).days).id,
});

export const getInputRange = (
  calendar: Calendar,
  input: DateInput,
  { departureDate, returnDate }: TravelDates = {}
): DateRange => {
  const { rangeStart, rangeEnd } = getCalendarRange(calendar);
  if (input === DateInput.DEPARTURE) {
    return { rangeStart, rangeEnd: returnDate || rangeEnd };
  }
  return { rangeStart: departureDate || rangeStart, rangeEnd };
};

// == Utilities ================================================================

export const totalPriceOptions: PriceFormatOptions = Object.freeze({
  round: true,
  showSymbol: true,
  showThousandSeparators: true,
});

export const selectionStartForDeparture = (calendar: Calendar, departureDate: string): number => {
  if (isBlank(departureDate)) {
    return 0;
  }
  const monthId: string = getMonth(departureDate);
  return Math.max(
    calendar.months.findIndex((month: Month) => month.id === monthId),
    0
  );
};

export const amSelectionStartForDeparture = (
  calendar: Calendar,
  departureDate: string,
  destination: AmLocation
): number =>
  isBlank(departureDate)
    ? selectionStartForDeparture(calendar, destination?.searchable[0]?.from || '')
    : selectionStartForDeparture(calendar, departureDate);

export const amDayIsDisabled = (
  day: Day,
  departureDate: string,
  returnDate: string,
  location: AmLocation,
  holidayType: AmHolidayType,
  availability: AmAvailability
): boolean => {
  if (!holidayType || holidayType.type === AmDurationType.LIST) {
    return !amDayIsSearchable(day.id, location);
  }
  return (
    // day must be searchable
    !amDayIsSearchable(day.id, location) ||
    // When selecting a departure date, the day must be available if we have availability data
    (isPresent(departureDate) === isPresent(returnDate) &&
      amAvailabilityIsPresent(availability) &&
      !amDayIsAvailable(day, availability, undefined)) ||
    // When selecting return date it must be within days limits from departure and available if we have availability data
    (isPresent(departureDate) &&
      !isPresent(returnDate) &&
      (daysBetween(departureDate, day.id) > holidayType.maxDays ||
        daysBetween(departureDate, day.id) < holidayType.minDays ||
        (amAvailabilityIsPresent(availability) && !amReturnDateIsAvailable(day, availability, departureDate))))
  );
};

export const amAvailabilityIsPresent = (amAvailability: AmAvailability): boolean =>
  !isEmpty(Object.keys(amAvailability));

export const amDayIsAvailable = (day: Day, amAvailability: AmAvailability, durationCode: string): boolean => {
  const durations = amAvailability?.[day.id];
  const isAvailable =
    (durations && !durationCode) || // No durationCode -> are there any durations for the day
    isPresent(durations?.[durationCode]);
  return isAvailable;
};

export const amReturnDateIsAvailable = (day: Day, amAvailability: AmAvailability, departureDate: string): boolean => {
  const durations = amAvailability?.[departureDate];
  return (
    durations &&
    Object.keys(durations)?.some((durationKey: string) => daysBetween(departureDate, day.id) === parseInt(durationKey))
  );
};
